/**
 * Full screen (the green dot, and the overflow's "Open full screen") for an
 * app window on the canvas.
 *
 * THE RULE (the owner, 2026-09-30): the green dot shows the app in the
 * PAGE's own full-screen view and never asks the BROWSER for full screen:
 * "clicking the green button should not switch the browser to full screen
 * mode, just the app to the full screen ui mode". The canvas does all of it
 * (lane D; the Stage algorithm, rules 12 to 17): a temporary screen of its
 * own right after the one the window came from, the rail and the window's
 * title bar hidden, lane B's app bar on top, the SAME node drawn at a
 * display-only size under it (the iframe never moves: zero loads), and
 * nothing written to the shared document. Esc on the page, the bar's exit
 * button and the ⋯ row "Exit full screen" leave it; an Esc pressed inside
 * the app never reaches the page and stays the app's.
 *
 * HOW WE GOT HERE, because each step is load-bearing:
 *
 *   - Until 2026-09-21 the dot hard-navigated to `/w/<contentId>?ws=&item=`,
 *     which mounts the app under a SYNTHETIC share id (`ephemeral`): the fn
 *     route billed the folder owner, about twenty bridge ops dead-ended, and a
 *     signed-in Revent attendee could not register from their own Revent.
 *   - From 2026-09-21 it asked the browser to present the app's
 *     `.react-flow__node` wrapper itself (`requestFullscreen()` on the node):
 *     measured with a `load` counter on the app iframe, that fired ZERO loads,
 *     where reparenting the frame to `<body>` (the in-canvas takeover's
 *     portal) fires one and remounts the app. A refused request grew the
 *     window to the viewport IN THE SHARED DOCUMENT (`toggleMaximizeToViewport`),
 *     so every other device saw the new size.
 *   - From 2026-09-29 (lane D) the PAGE was the browser's full-screen
 *     element, with Keyboard Lock taking Esc where the browser had it.
 *   - Since 2026-09-30 the browser is not asked at all (the rule above). A
 *     person can still put the browser in full screen themselves (⌃⌘F,
 *     F11): the page's view does not react to it and does not end with it.
 *
 * What is left here is the one read other code still needs: which element,
 * if any, the browser is presenting full screen (an app's own video, the
 * deck presenter, or the person's own browser full screen), for
 * components/workspace/fullscreen/use-document-fullscreen.ts. Pinned by
 * tests/unit/workspaces/app-node-maximize-native-fullscreen.test.ts.
 */

/** The element the document is presenting fullscreen, unprefixed or Safari. */
export function currentFullscreenElement(doc: Document = document): Element | null {
  const d = doc as Document & { webkitFullscreenElement?: Element | null };
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}
